import { Box, Stack, Typography } from '@mui/material';
import ChevronRightRounded from '@mui/icons-material/ChevronRightRounded';
import { formatDollarAmount } from '../../utils/global/helper';
import { CheckoutFormItem } from '../form/CheckoutFormItems';
import { CheckoutDiscountLine } from '../../types/checkouts';

interface Props {
  title: string;
  items: CheckoutFormItem[];
  discount?: CheckoutDiscountLine;
}

/** What the line contributes to the cart: items are priced per unit. */
const getItemTotal = (item: CheckoutFormItem) =>
  item.type === 'item' ? item.price * item.quantity : item.price;

/** What a card shows, whichever kind of line it came from. */
interface LineCard {
  id: string;
  kind: 'Service' | 'Item' | 'Discount';
  name: string;
  meta?: string;
  total: number;
  onClick?: () => void;
}

function toCard(item: CheckoutFormItem): LineCard {
  if (item.type === 'item') {
    return {
      id: item.id,
      kind: 'Item',
      name: item.name,
      meta:
        item.quantity > 1
          ? `${formatDollarAmount(item.price)} each · Qty ${item.quantity}`
          : undefined,
      total: getItemTotal(item),
      onClick: item.onClick,
    };
  }
  return {
    id: item.id,
    kind: 'Service',
    name: item.name,
    meta: [
      item.provider,
      item.commissionRate ? `${item.commissionRate} commission` : undefined,
    ]
      .filter(Boolean)
      .join(' · '),
    total: getItemTotal(item),
    onClick: item.disabled ? undefined : item.onClick,
  };
}

function LineItemCard({ card }: { card: LineCard }) {
  const isClickable = !!card.onClick;

  return (
    <Box
      role={isClickable ? 'button' : undefined}
      onClick={card.onClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        padding: '15px 18px',
        border: '1px solid var(--darkerLightGrey)',
        borderRadius: '10px',
        background: theme => theme.palette.common.white,
        cursor: isClickable ? 'pointer' : 'default',
        transition: 'background 120ms ease, border-color 120ms ease',
        '&:hover': isClickable
          ? {
              background: 'var(--lightGrey)',
              borderColor: 'var(--lightOutlineGrey)',
            }
          : undefined,
      }}>
      <Stack gap="5px" minWidth={0}>
        <Stack direction="row" alignItems="center" gap={1.25}>
          <Typography variant="h4" lineHeight={1} noWrap>
            {card.name}
          </Typography>
          <Typography
            variant="caption"
            component="span"
            fontWeight={600}
            lineHeight={1}
            flexShrink={0}
            sx={{
              padding: '4px 8px',
              borderRadius: '5px',
              background: 'var(--lightGrey)',
              color: 'text.tertiary',
            }}>
            {card.kind}
          </Typography>
        </Stack>
        {!!card.meta && (
          <Typography
            variant="caption"
            lineHeight={1.3}
            sx={{ color: 'text.tertiary' }}>
            {card.meta}
          </Typography>
        )}
      </Stack>
      <Stack
        direction="row"
        alignItems="center"
        gap={1.75}
        marginLeft="auto"
        flexShrink={0}>
        <Typography
          variant="h4"
          sx={{
            color: theme =>
              card.total < 0 ? theme.palette.secondary.main : undefined,
          }}>
          {formatDollarAmount(card.total)}
        </Typography>
        {isClickable && (
          <ChevronRightRounded
            sx={{ fontSize: 18, color: 'var(--outlineGrey)' }}
          />
        )}
      </Stack>
    </Box>
  );
}

export default function CheckoutLineItems({ title, items, discount }: Props) {
  const cards = items.map(toCard);
  if (discount) {
    cards.push({
      id: discount.id,
      kind: 'Discount',
      name: discount.name,
      meta: discount.meta,
      total: discount.amount,
      onClick: discount.onClick,
    });
  }

  const count = `${cards.length} ${cards.length === 1 ? 'item' : 'items'}`;
  // The hint only earns its place when something can actually be clicked.
  const countLabel = cards.some(card => !!card.onClick)
    ? `${count} · click a card to edit`
    : count;

  return (
    <Stack gap={1.5} width="100%">
      <Stack
        direction="row"
        alignItems="baseline"
        justifyContent="space-between"
        width="100%"
        gap={2}>
        <Typography variant="h2" component="h2">
          {title}
        </Typography>
        <Typography
          variant="caption"
          fontWeight={600}
          flexShrink={0}
          sx={{ color: 'var(--medGrey)' }}>
          {countLabel}
        </Typography>
      </Stack>
      {cards.map(card => (
        <LineItemCard key={card.id} card={card} />
      ))}
    </Stack>
  );
}
